import type Vue from 'vue';
import { setGlobalSentry } from './sentryGlobal';
import { initializeUploadTracking } from './sentryUploadTracking';
import { initializeGeneralSentryMonitoring } from './sentryUtils';

interface SentryInitOptions {
  vue: typeof Vue;
  tenantId?: string;
  isSharePage?: boolean;
}

/**
 * Initialize Sentry error tracking with standard configuration
 * @param options Configuration options for Sentry initialization
 */
export async function initializeSentry(options: SentryInitOptions): Promise<void> {
  if (window.__sentryInitialized) {
    console.warn('Sentry already initialized, skipping...');
    return;
  }

  try {
    // Dynamic import to load Sentry only when needed
    const Sentry = await import('@sentry/vue');

    // Set the Sentry instance globally
    setGlobalSentry(Sentry);

    Sentry.init({
      Vue: options.vue,
      dsn: 'https://d637dda82e8398e7ac3a0beac17d6ac6@o4509709019447296.ingest.de.sentry.io/4509709034651728',
      environment: undefined, // Don't track environment - URLs provide this context
      sendDefaultPii: false,
      integrations: [
        // No integrations - we only want error tracking, not performance monitoring
      ],
      // Additional options to prevent IP collection
      initialScope: {
        tags: {},
        extra: {},
      },
      // Disable performance monitoring - only send data when errors occur
      tracesSampleRate: 0,
      // Disable distributed tracing since we only use Sentry on client side
      tracePropagationTargets: [],
      beforeSend(event) {
        // Remove user data entirely for GDPR compliance
        delete event.user;
        return event;
      },
    });

    // Clear any user context to prevent IP tracking
    Sentry.setUser(null);

    // Set global context tags for all Sentry events
    if (options.isSharePage) {
      Sentry.setTag('context', 'share_page');
    } else if (options.tenantId) {
      Sentry.setTag('tenant_id', options.tenantId);
    }

    // Initialize general Sentry monitoring (network, visibility)
    initializeGeneralSentryMonitoring();

    // Initialize upload tracking for Sentry
    initializeUploadTracking();

    // Mark as initialized to prevent multiple initializations
    window.__sentryInitialized = true;

    console.log('Sentry initialized successfully');
  } catch (error) {
    console.error('Failed to initialize Sentry:', error);
  }
}
